<template>
  <navigation_back class="mb-2" />
  <div v-if="survey">
    <div class="gap-4 bg-gray-600 font-bold text-white px-6 py-4 rounded-t-md">
      <h3 class="text-xl font-semibold">
        Survey: {{ survey.survey_type_name }}
      </h3>
      <h2 class="font-normal">{{survey.related_object_name}}</h2>
    </div>
    <div class="mb-2 col-span-5 bg-white shadow px-9 py-6">
      <div class="flex items-center justify-between gap-2 mb-2 mt-4">
        <h3 class="font-semibold text-lg">Survey Questions</h3>
        <div class="flex gap-2">
          <button 
            class="btn-primary-sm"
            @click="saveAllChanges"
            :disabled="!hasUnsavedChanges"
          >
            Save All
          </button>
          <button 
            class="btn-secondary-sm"
            @click="cancelChanges"
            :disabled="!hasUnsavedChanges"
          >
            Cancel
          </button>
        </div>
      </div>
      <div class="mt-4">
        <div
          v-for="answer in surveyAnswers"
          :key="answer.id"
          class="mb-6"
        >
          <div>
            <div class="mb-2 flex justify-between gap-4 items-center">
              <div class="w-full flex gap-2">
                <label class="font-medium">{{answer.survey_question_name}}</label>
              </div>
            </div>
            
            <!-- Text Question -->
            <textarea
              v-if="answer.survey_question_type === 'text'"
              v-model="answer.response"
              @input="markAsChanged(answer)"
              class="w-full rounded-md h-32 bg-gray-50 border px-2 py-1"
              placeholder="Enter response..."
            />
            
            <!-- Single Choice Question -->
            <div v-else-if="answer.survey_question_type === 'single_choice'" class="space-y-2">
              <div 
                v-for="(text, id) in answer.survey_question_choices" 
                :key="id"
                class="flex items-center gap-2"
              >
                <input
                  type="radio"
                  :id="`${answer.id}-${id}`"
                  :name="`question-${answer.id}`"
                  :value="id"
                  v-model="answer.selectedChoice"
                  @change="markAsChanged(answer)"
                  class="w-4 h-4"
                />
                <label :for="`${answer.id}-${id}`" class="cursor-pointer">{{ text }}</label>
              </div>
            </div>
            
            <!-- Multiple Choice Question -->
            <div v-else-if="answer.survey_question_type === 'multiple_choice'" class="space-y-2">
              <div 
                v-for="(text, id) in answer.survey_question_choices" 
                :key="id"
                class="flex items-center gap-2"
              >
                <input
                  type="checkbox"
                  :id="`${answer.id}-${id}`"
                  :value="id"
                  v-model="answer.selectedChoices"
                  @change="markAsChanged(answer)"
                  class="w-4 h-4"
                />
                <label :for="`${answer.id}-${id}`" class="cursor-pointer">{{ text }}</label>
              </div>
            </div>
            
            <div class="text-xs text-gray-500 mt-1">
              <span v-if="answer.hasUnsavedChanges" class="text-orange-600">
                Unsaved changes
              </span>
              <span v-else-if="answer.lastSaved">
                Last saved at {{ formatDate(answer.lastSaved) }}
              </span>
              <span v-else>
                Not yet saved
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import navigation_back from '@/components/widgetcomponents/navigation_back.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue'

export default {
  components: {
    ConfirmationModal,
    FontAwesomeIcon,
    navigation_back,
  },
  props: {
    survey_id:{
      type:String,
      required:true
    }
  },
  data() {
    return {
      isOpenDeleteConfirmationModal: false,
      deletePracticeQuestionId: null,
      survey: {},
      surveyAnswers: [],
      originalAnswers: [], // Store original state for cancel
    }
  },
  computed: {
    hasUnsavedChanges() {
      return this.surveyAnswers.some(answer => answer.hasUnsavedChanges)
    }
  },
  methods: {
    async fetchSurvey() {
      try {
        const response = await axios.get(`/api/surveys/${this.survey_id}/`)
        this.survey = response.data

        // Create a deep copy of answers with additional properties
        if (this.survey.answers && Array.isArray(this.survey.answers)) {
          this.surveyAnswers = this.survey.answers.map(answer => this.processAnswer(answer))
          // Store original state for cancel functionality
          this.originalAnswers = JSON.parse(JSON.stringify(this.surveyAnswers))
        }
      } catch (error) {
        const { status } = error.response
        if (status === 403) {
          this.$router.push({ name: 'ForbiddenPage' })
        } else if (status === 404) {
          this.$router.push({ name: 'NotFound' })
        } else if (status >= 500) {
          this.$router.push({ name: 'ErrorPage' })
        }
        console.error('Error fetching survey:', error)
      }
    },
    
    processAnswer(answer) {
      const processed = {
        ...answer,
        lastSaved: null,
        hasUnsavedChanges: false,
      }
      
      // Parse response for choice questions
      if (answer.survey_question_type === 'single_choice') {
        try {
          const parsed = answer.response ? JSON.parse(answer.response) : {}
          processed.selectedChoice = Object.keys(parsed)[0] || null
        } catch {
          processed.selectedChoice = null
        }
      } else if (answer.survey_question_type === 'multiple_choice') {
        try {
          const parsed = answer.response ? JSON.parse(answer.response) : {}
          processed.selectedChoices = Object.keys(parsed)
        } catch {
          processed.selectedChoices = []
        }
      }
      
      return processed
    },
    
    markAsChanged(answer) {
      answer.hasUnsavedChanges = true
    },
    
    async saveAllChanges() {
      const changedAnswers = this.surveyAnswers.filter(a => a.hasUnsavedChanges)
      
      try {
        await Promise.all(changedAnswers.map(answer => this.saveResponse(answer)))
        
        // Update original state after successful save
        this.originalAnswers = JSON.parse(JSON.stringify(this.surveyAnswers))
      } catch (error) {
        console.error('Error saving responses:', error)
      }
    },
    
    async saveResponse(answer) {
      let responseData = answer.response
      
      // Convert choice selections to JSON format
      if (answer.survey_question_type === 'single_choice') {
        if (answer.selectedChoice) {
          const choiceText = answer.survey_question_choices[answer.selectedChoice]
          responseData = JSON.stringify({ [answer.selectedChoice]: choiceText })
        } else {
          responseData = null
        }
      } else if (answer.survey_question_type === 'multiple_choice') {
        if (answer.selectedChoices && answer.selectedChoices.length > 0) {
          const choices = {}
          answer.selectedChoices.forEach(id => {
            choices[id] = answer.survey_question_choices[id]
          })
          responseData = JSON.stringify(choices)
        } else {
          responseData = null
        }
      }
      
      await axios.patch(`/api/surveyanswers/${answer.id}/`, {
        response: responseData,
      })
      
      answer.lastSaved = new Date()
      answer.hasUnsavedChanges = false
      answer.response = responseData
    },
    
    cancelChanges() {
      // Restore from original state
      this.surveyAnswers = JSON.parse(JSON.stringify(this.originalAnswers))
    },
    
    formatDate(date) {
      return new Date(date).toLocaleString()
    },
  },
  mounted() {
    this.fetchSurvey()
  },
}
</script>

<style scoped>
/* Add any additional styles if needed */
</style>
